import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Assessment as ReportIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { api } from '../config/api';
import DailyContainerReport from './DailyContainerReport';
import ContainerNav from './ContainerNav';

interface Container {
  id: number;
  container_no: string;
  seal_no: string;
  primary_po_number?: string | null;  // Multi-PO system: primary PO
  supplier_code?: string;
  delivery_day: string;
  delivery_time: string;
  dock: string;
  status: string;
  completed_at?: string;
  arrival_time?: string;
  departure_time?: string;
  signed_by_name?: string;
  line_count: number;
  // Multi-PO fields
  po_numbers?: string[];
  is_shared?: boolean;
  // Batch fields
  batch_description?: string | null;
  batch_is_merged?: boolean;
}

// Helper to check if container is received (handles old, new, and archived status values)
const isContainerReceived = (status: string | undefined): boolean => {
  return status === 'received' || status === 'Completed' || status === 'Finalised' || status === 'archived';
};

const ContainerReports: React.FC = () => {
  const navigate = useNavigate();
  const [containers, setContainers] = useState<Container[]>([]);
  const [filteredContainers, setFilteredContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dockFilter, setDockFilter] = useState<string>('all');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [sendingReportEmail, setSendingReportEmail] = useState<number | null>(null);

  useEffect(() => {
    loadContainers();
  }, []);

  useEffect(() => {
    filterContainers();
  }, [searchTerm, dockFilter, containers]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/containers-flat?sort=desc`);

      if (response.data.success) {
        // Filter to only show received containers
        const completedContainers = response.data.containers.filter(
          (c: Container) => isContainerReceived(c.status)
        );
        setContainers(completedContainers);
        setFilteredContainers(completedContainers);
      } else {
        setError('Failed to load containers');
      }
    } catch (err) {
      console.error('Error loading containers:', err);
      setError('Error loading containers');
    } finally {
      setLoading(false);
    }
  };

  const sendReportEmail = async (containerId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click from triggering
    try {
      setSendingReportEmail(containerId);
      setError(null);
      setSuccess(null);
      const response = await api.post(`/containers/${containerId}/send-report-email`);
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.error || 'Failed to send report email');
      }
    } catch (err: any) {
      console.error('Error sending report email:', err);
      setError(err.response?.data?.error || 'Error sending report email');
    } finally {
      setSendingReportEmail(null);
    }
  };

  const filterContainers = () => {
    let filtered = containers;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.container_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.primary_po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.seal_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.supplier_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Dock filter
    if (dockFilter !== 'all') {
      filtered = filtered.filter(c => c.dock === dockFilter);
    }

    setFilteredContainers(filtered);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;

    return `${dayName} ${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleViewReport = async (containerId: number) => {
    try {
      setLoadingReport(true);
      setReportOpen(true);

      // Fetch individual container data and build report structure
      const containerResponse = await api.get(`/containers/${containerId}`);
      const completionResponse = await api.get(`/containers/${containerId}/completion`);
      const linesResponse = await api.get(`/containers/${containerId}/lines`);
      const locationsResponse = await api.get(`/containers/${containerId}/line-locations`);
      const photosResponse = await api.get(`/containers/${containerId}/photos`);

      // Group lines by stock_code and add put_away_locations
      const linesData = linesResponse.data.lines || [];
      const locationsData = locationsResponse.data.line_locations || [];

      const stockDetailsMap: any = {};
      linesData.forEach((line: any) => {
        if (!stockDetailsMap[line.stock_code]) {
          stockDetailsMap[line.stock_code] = {
            stock_code: line.stock_code,
            rolls_per_pallet: line.rolls_per_pallet,
            total_sheets: 0,
            total_metres: 0,
            total_weight_tonnes: 0,
            pallet_count: 0,
            put_away_locations: []
          };
        }
        stockDetailsMap[line.stock_code].total_sheets += line.sheets || 0;
        stockDetailsMap[line.stock_code].total_metres += line.metres || 0;
        stockDetailsMap[line.stock_code].total_weight_tonnes += line.net_weight_tonnes || 0;
        stockDetailsMap[line.stock_code].pallet_count += 1;
      });

      // Add put_away_locations to each stock code
      locationsData.forEach((loc: any) => {
        if (stockDetailsMap[loc.stock_code]) {
          stockDetailsMap[loc.stock_code].put_away_locations.push({
            location_name: loc.location_name,
            quantity: loc.quantity,
            measurement_value: loc.measurement_value,
            measurement_unit: loc.measurement_unit
          });
        }
      });

      // Build report data structure matching DailyContainerReport format
      setReportData({
        container: containerResponse.data.container,
        completion: {
          ...completionResponse.data.completion,
          completed_by_name: completionResponse.data.completion.signed_by_name || completionResponse.data.completion.signed_by_staff_no
        },
        stock_details: Object.values(stockDetailsMap),
        photos: {
          seal_photos: photosResponse.data.photos?.filter((p: any) => p.photo_type === 'seal').map((p: any) => p.photo_url) || [],
          concern_photos: photosResponse.data.photos?.filter((p: any) => p.photo_type === 'damage').map((p: any) => p.photo_url) || []
        }
      });
    } catch (err) {
      console.error('Error loading container report:', err);
      setError('Error loading container report');
    } finally {
      setLoadingReport(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <ContainerNav />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Search containers, PO, seal, supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1, minWidth: 250 }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Dock</InputLabel>
            <Select
              value={dockFilter}
              label="Dock"
              onChange={(e) => setDockFilter(e.target.value)}
            >
              <MenuItem value="all">All Docks</MenuItem>
              <MenuItem value="Dock 1">Dock 1</MenuItem>
              <MenuItem value="Dock 2">Dock 2</MenuItem>
              <MenuItem value="Dock 3">Dock 3</MenuItem>
              <MenuItem value="Dock 4">Dock 4</MenuItem>
              <MenuItem value="Dock 5">Dock 5</MenuItem>
            </Select>
          </FormControl>

          <Chip
            label={`${filteredContainers.length} containers`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Results Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Container #</strong></TableCell>
              <TableCell><strong>PO #</strong></TableCell>
              <TableCell><strong>Supplier</strong></TableCell>
              <TableCell><strong>Seal #</strong></TableCell>
              <TableCell><strong>Dock</strong></TableCell>
              <TableCell><strong>Completed</strong></TableCell>
              <TableCell><strong>Signed By</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredContainers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    {searchTerm || dockFilter !== 'all'
                      ? 'No containers match your filters'
                      : 'No completed containers found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredContainers.map((container) => (
                <TableRow
                  key={container.id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                  onClick={() => handleViewReport(container.id)}
                >
                  <TableCell>{container.container_no}</TableCell>
                  <TableCell>{container.primary_po_number || 'N/A'}</TableCell>
                  <TableCell>{container.supplier_code || 'N/A'}</TableCell>
                  <TableCell>{container.seal_no || 'N/A'}</TableCell>
                  <TableCell>{container.dock}</TableCell>
                  <TableCell>{formatDateTime(container.completed_at)}</TableCell>
                  <TableCell>{container.signed_by_name || 'N/A'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={(e) => sendReportEmail(container.id, e)}
                      title="Send Report Email"
                      size="small"
                      disabled={sendingReportEmail === container.id}
                    >
                      {sendingReportEmail === container.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <EmailIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Container Report Dialog - uses DailyContainerReport component */}
      <DailyContainerReport
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        reportDate={new Date().toISOString()}
        containers={reportData ? [reportData] : []}
      />
    </Box>
  );
};

export default ContainerReports;
